import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getTranslationValue } from '../../../constants/posMenu';
import { isItemSoldOut } from './previewHelpers';
import { POS_COLORS } from './posColors';

/**
 * Items – ONE horizontal row with horizontal scroll (matches POS client).
 * Colors from POS (posColors.js).
 */
const ITEM_BTN_W = 130;
const ITEM_BTN_H = 74;

const ProductsGrid = ({
  menuMap,
  selectedCategory,
  selectedProduct,
  onSelectProduct,
}) => {
  const products = React.useMemo(() => {
    if (!selectedCategory || !menuMap?.items) return [];
    const itemIds = selectedCategory.itemIds || [];
    return itemIds.map((id) => menuMap.items[id]).filter(Boolean);
  }, [selectedCategory, menuMap]);

  if (!selectedCategory) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a category to view items
        </Typography>
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No items in this category
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1, py: 1 }}>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: POS_COLORS.BLACK,
          mb: 1,
        }}
      >
        Items
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: POS_COLORS.GRAY_SLIDE,
            borderRadius: 3,
          },
        }}
      >
        {products.map((product) => {
          const isSelected = selectedProduct?._id === product._id;
          const isSoldOut = isItemSoldOut(product);

          return (
            <Box
              key={product._id}
              onClick={() => {
                if (!isSoldOut) onSelectProduct(product);
              }}
              sx={{
                flexShrink: 0,
                width: ITEM_BTN_W,
                height: ITEM_BTN_H,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                position: 'relative',
                bgcolor: isSoldOut
                  ? POS_COLORS.DISABLED_BG
                  : isSelected
                  ? POS_COLORS.PRODUCT_BG_SELECTED
                  : POS_COLORS.PRODUCT_BG,
                color: POS_COLORS.WHITE,
                cursor: isSoldOut ? 'not-allowed' : 'pointer',
                opacity: isSoldOut ? 0.7 : 1,
                transition: 'all 0.15s ease',
                border: 'none',
                '&:hover': {
                  bgcolor: isSoldOut ? undefined : POS_COLORS.PRODUCT_BG_HOVER,
                },
              }}
            >
              {isSelected && !isSoldOut && (
                <CheckCircleIcon
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: 20,
                    color: POS_COLORS.WHITE,
                  }}
                />
              )}
              {isSoldOut && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    fontSize: 10,
                    fontWeight: 600,
                    color: POS_COLORS.ERROR,
                  }}
                >
                  Unavailable
                </Typography>
              )}
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: 16,
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {getTranslationValue(product.name)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: POS_COLORS.WHITE,
                }}
              >
                ${(product.price || 0).toFixed(2)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProductsGrid;
